/**
 * THE PANEL WHILE SHE IS TAKEN TO STRIPE'S PAGE, and the poll that waits on the page: one for the two
 * places that ask ops for a page of Stripe's, the bank and the card on an invoice (`StripeWay.tsx`,
 * slices V3 and M3, P46), and the offer to save a way to pay with nothing owed on How you pay
 * (`SaveMethod.tsx`, round five, slice A5, ops's section 12.2, which home polls "as for
 * `invoice.pay`"). Moved here from `StripeWay.tsx` unchanged, so both draw and time it alike.
 *
 * - THE PANEL OVER THE SCREEN, the moment a press goes: the screen below the header dims, the page
 *   and the nav go inert (`useCover`, `Shell.tsx`), and a panel in its middle takes the focus and
 *   says, in a live region, "One moment", and where she is being taken (`going`); after about eight
 *   seconds, that it is taking longer than usual and to keep the page open. The header stays live
 *   above it, Call with it. Built after the owner's walk of 2026-10-16, where only the pointer over
 *   the button said anything was happening.
 * - "ONE MOMENT" ALONE while a press behind a fresh code has not been taken by the door, or the code
 *   is checked (`asking`), and with where she is going once it has (`going`).
 * - EVERY END SAYS WHAT TO DO in the live region in place of its lines, as the screen words it
 *   (`ended`), with Try again where the screen offers it (`retry`), which presses again, and Close,
 *   or Escape, which gives the screen back with the focus where the screen puts it (`focusBack`).
 *   While she waits there is nothing to close.
 * - A BROWSER THAT BRINGS THE PAGE BACK FROM ITS CACHE, as the back button can, takes the panel away:
 *   the press that left is over.
 * - THE POLL asks the Worker where the page stands every three seconds and at most six times, each
 *   ask answered from what the Worker has stored and never waiting on ops (isolation rule 5). A poll
 *   that did not go through, or never reached the Worker, is asked again within the six; one with no
 *   answer in its time (`REQUEST_LIMIT_MS`, `api.ts`) ends the press, as not gone through; once the
 *   six are spent, the press ends as the last ask said: the page still being made, not gone through,
 *   or the connection dropped (`pollForPage`).
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import type { PayPageAnswer } from '../api.ts';
import { CLOSE, PAYING } from '../words.ts';
import { GHOST_BUTTON, NAVY_BUTTON } from './buttons.ts';
import { COVER_LAYER, useCover } from './Shell.tsx';

/** How long the screen waits between asks where the page stands, as ops replays (l.665). */
export const POLL_GAP_MS = 3000;

/** How many times at most the screen asks, after which it says to try again in a minute. */
export const POLLS = 6;

/** How long the panel says only where she is going, before it says it is slow. */
export const LONGER_AFTER_MS = 8000;

/**
 * What the panel says while she waits: "One moment" alone, while a press behind a code has not been
 * taken by the door, or the code is checked; and with it where she is going, once it has, and for
 * every press that needs no code.
 */
export type Waiting = 'asking' | 'going';

/** Whether a line is said while she waits, rather than how the press ended. */
export const isWaiting = (line: unknown): line is Waiting => line === 'asking' || line === 'going';

/** The panel's state and its ways, for the screen that draws it. `E` is the screen's own ends. */
export interface Leaving<E extends string> {
  /** What the panel says, or null where no panel is drawn. */
  readonly line: Waiting | E | null;
  /** The panel says she waits. */
  readonly waiting: boolean;
  /** The panel has said she waits for about eight seconds: it is taking longer than usual. */
  readonly longer: boolean;
  /** A press is out, so another does nothing. Read at once, never on the next draw. */
  readonly out: MutableRefObject<boolean>;
  /** The panel's box, which takes the focus as the panel comes up. */
  readonly box: RefObject<HTMLDivElement | null>;
  /**
   * The panel says she waits, at `stage`: it comes up, or comes back from how a press ended, and
   * after about eight seconds of waiting says it is taking longer than usual; a wait that goes on,
   * from "One moment" alone to where she is going, keeps the time it has waited.
   */
  wait(stage: Waiting): void;
  /** The press has ended, as `next` says in the panel, or with the panel gone. */
  finish(next: E | null): void;
  /** She closes the panel once it has said how the press ended: never while she waits. */
  close(): void;
  /** Starts a press, and gives back whether it is still the current one. */
  begin(): () => boolean;
}

/**
 * The panel's state, and the effects that go with it: the page and the nav inert while it is there,
 * the focus to its box as it comes up and to `focusBack` once she has closed it, and the panel taken
 * away when the browser brings the page back from its cache.
 */
export function useLeaving<E extends string>(focusBack: () => void): Leaving<E> {
  const [line, setLine] = useState<Waiting | E | null>(null);
  const [longer, setLonger] = useState(false);
  const longerTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** The panel says she waits, as it was last told, read at once rather than on the next draw. */
  const waitingNow = useRef(false);
  const out = useRef(false);
  /** Counts the presses, so a press that a later one or the page's return has replaced stops. */
  const run = useRef(0);
  const mounted = useRef(true);
  const box = useRef<HTMLDivElement>(null);
  /** The panel was drawn when the focus was last given, so it is not taken again while it stays. */
  const wasShown = useRef(false);
  /** The panel said she waits when the focus was last given, so a wait after an end takes it. */
  const wasWaiting = useRef(false);
  /** She closed the panel, so the screen's own place takes the focus back. */
  const closed = useRef(false);
  const focusBackNow = useRef(focusBack);
  focusBackNow.current = focusBack;
  const cover = useCover();
  const shown = line !== null;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(longerTimer.current);
    };
  }, []);

  // The page and the nav go inert as the panel comes, before any focus is given, and come back as
  // it goes, however it goes: closed, ended by a refusal, or the screen gone from under it.
  useLayoutEffect(() => {
    if (!shown) return;
    cover(true);
    return () => cover(false);
  }, [shown, cover]);

  // The panel takes the focus as it comes up, so what it says is read, and again as Try again sends
  // a press from it, the Try again pressed going with the end it said; once she closes it, the
  // screen's own place has the focus back.
  useEffect(() => {
    const nowWaiting = isWaiting(line);
    if (line !== null && (!wasShown.current || (nowWaiting && !wasWaiting.current))) {
      box.current?.focus();
    }
    if (line === null && closed.current) focusBackNow.current();
    closed.current = false;
    wasShown.current = line !== null;
    wasWaiting.current = nowWaiting;
  }, [line]);

  function finish(next: E | null) {
    clearTimeout(longerTimer.current);
    waitingNow.current = false;
    out.current = false;
    setLonger(false);
    setLine(next);
  }

  // A page the browser brings back from its cache after she left for Stripe's page offers its
  // button again, with the focus the panel had: the press that left is over.
  useEffect(() => {
    function cached(event: PageTransitionEvent) {
      if (!event.persisted) return;
      run.current += 1;
      closed.current = true;
      finish(null);
    }
    window.addEventListener('pageshow', cached);
    return () => window.removeEventListener('pageshow', cached);
  }, []);

  function wait(stage: Waiting) {
    if (!waitingNow.current) {
      clearTimeout(longerTimer.current);
      setLonger(false);
      longerTimer.current = setTimeout(() => setLonger(true), LONGER_AFTER_MS);
    }
    waitingNow.current = true;
    setLine(stage);
  }

  function close() {
    if (out.current) return;
    closed.current = true;
    setLine(null);
  }

  function begin(): () => boolean {
    out.current = true;
    run.current += 1;
    const mine = run.current;
    return () => mounted.current && run.current === mine;
  }

  return { line, waiting: isWaiting(line), longer, out, box, wait, finish, close, begin };
}

interface LeavingPanelProps<E extends string> {
  readonly leaving: Leaving<E>;
  /** What the panel says under "One moment" once the door has the ask: where she is going. */
  readonly going: string;
  /** What the panel says of how a press ended, the office's number with it where it has one. */
  readonly ended: (line: E) => ReactNode;
  /** Whether the panel offers to try again after this end. */
  readonly retry: (line: E) => boolean;
  /** Try again: the press again, a new request or the same one where the door was not reached. */
  readonly onTryAgain: () => void;
}

/** The panel, drawn over the screen below the header while there is anything for it to say. */
export function LeavingPanel<E extends string>({
  leaving,
  going,
  ended,
  retry,
  onTryAgain,
}: LeavingPanelProps<E>) {
  const { line, waiting, longer, box, close } = leaving;
  if (line === null) return null;
  return createPortal(
    <div
      data-leaving
      className={`fixed inset-0 ${COVER_LAYER} flex items-center justify-center bg-surface-brand-strong/55 px-5 ${
        waiting ? 'cursor-wait' : ''
      }`}
    >
      <div
        ref={box}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || waiting) return;
          event.stopPropagation();
          close();
        }}
        className="w-full max-w-md rounded-card bg-surface-raised px-6 py-8 text-center shadow-[0_4px_16px_rgb(16_34_55/0.2)]"
      >
        {waiting ? (
          <span
            aria-hidden="true"
            className="mx-auto mb-5 block size-10 animate-spin rounded-full border-4 border-surface-brand-soft border-t-surface-brand motion-reduce:animate-none"
          />
        ) : null}
        <div role="status">
          {waiting ? (
            <>
              <p className="text-lead font-semibold text-pretty text-text-body">
                {PAYING.oneMoment}
              </p>
              {line === 'going' ? (
                <p className="mt-1 text-body text-pretty text-text-body">{going}</p>
              ) : null}
              {longer ? (
                <p className="mt-3 text-body text-pretty text-text-muted">
                  {PAYING.longerThanUsual}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-body text-pretty text-text-body">{ended(line as E)}</p>
          )}
        </div>
        {waiting ? null : (
          <div className="mt-6 flex flex-col gap-3">
            {retry(line as E) ? (
              <button
                type="button"
                onClick={onTryAgain}
                className={`cursor-pointer ${NAVY_BUTTON}`}
              >
                {PAYING.tryAgain}
              </button>
            ) : null}
            <button type="button" onClick={close} className={`cursor-pointer ${GHOST_BUTTON}`}>
              {CLOSE}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** What the last ask where the page stands said, which says how the poll ends once spent. */
type LastAsked = 'waiting' | 'unread' | 'offline';

/** How a press ends once the poll is spent, or a poll ends with no answer in its time. */
export type PollEnded = 'failed' | 'slow' | 'offline';

/** How a press ends once the poll is spent, by what its last ask said. */
const SPENT: Readonly<Record<LastAsked, PollEnded>> = {
  waiting: 'slow',
  unread: 'failed',
  offline: 'offline',
};

/** What came of the poll, for the screen to act on. */
export type Polled =
  /** The press is no longer the current one: nothing is to be done. */
  | { readonly outcome: 'gone' }
  /** The page, at its https address: she goes there. */
  | { readonly outcome: 'ready'; readonly url: string }
  /** The session has ended. */
  | { readonly outcome: 'signed_out' }
  /** The ask is not hers to ask about now. */
  | { readonly outcome: 'not_found' }
  /** Ops refused it as final, with its word, or home's own `expired`. */
  | { readonly outcome: 'refused'; readonly code?: string }
  /** It ended as the panel says: not gone through, still being made, or the connection dropped. */
  | { readonly outcome: 'ended'; readonly as: PollEnded };

/**
 * Asks where the page stands, every three seconds and six times at most (`read`), while the press
 * is still the current one, and gives back what came of it. No page coming and an ask home gave up
 * sending end it as not gone through.
 */
export async function pollForPage(
  read: () => Promise<PayPageAnswer>,
  current: () => boolean,
): Promise<Polled> {
  let last: LastAsked = 'waiting';
  for (let asked = 0; asked < POLLS; asked += 1) {
    await new Promise((resolve) => setTimeout(resolve, POLL_GAP_MS));
    if (!current()) return { outcome: 'gone' };
    const answer = await read();
    if (!current()) return { outcome: 'gone' };
    switch (answer.outcome) {
      case 'waiting':
      case 'unread':
      case 'offline':
        last = answer.outcome;
        continue;
      case 'stalled':
        return { outcome: 'ended', as: 'failed' };
      case 'ready':
        return { outcome: 'ready', url: answer.url };
      case 'signed_out':
        return { outcome: 'signed_out' };
      case 'not_found':
        return { outcome: 'not_found' };
      case 'refused':
        return answer.code === undefined
          ? { outcome: 'refused' }
          : { outcome: 'refused', code: answer.code };
      case 'none':
      case 'failed':
        return { outcome: 'ended', as: 'failed' };
    }
  }
  return { outcome: 'ended', as: SPENT[last] };
}
